import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';

interface Props { auth: boolean; access: any; currency?: string | null; }

interface Row {
  key: string;
  kind: 'expense' | 'mileage';
  date: string;
  title: string;
  distance?: string;
  amount: number;
  status: string;
  reason?: string | null;
}

// Finance statuses: pending → fulfilled (approved) → paid, or rejected
const STATUS: Record<string, { label: string; cls: string }> = {
  pending: { label: 'Pending', cls: 'p' },
  fulfilled: { label: 'Approved', cls: 'a' },
  paid: { label: 'Paid', cls: 'd' },
  rejected: { label: 'Rejected', cls: 'r' },
};

const ROW_H = 47;

const can = (access: any, block: string) =>
  [access?.[`${block}_view`], access?.[`${block}_admin`]].some(v => v === true || v === 1);

const ExpensesCard: React.FC<Props> = ({ auth, access, currency }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const canExp = can(access, 'expenses');
  const canCar = can(access, 'car_exp');

  const load = useCallback(async () => {
    const [exp, car] = await Promise.allSettled([
      canExp ? axios.get('expenses/my-recent') : Promise.resolve({ data: [] }),
      canCar ? axios.get('cars-expenses/my-recent') : Promise.resolve({ data: [] }),
    ]);
    const list: Row[] = [];
    if (exp.status === 'fulfilled' && Array.isArray(exp.value.data)) {
      exp.value.data.forEach((e: any) => list.push({
        key: `e${e.id}`, kind: 'expense', date: e.date, title: e.description || 'Expense',
        amount: Number(e.amount) || 0, status: e.status, reason: e.status_comment,
      }));
    }
    if (car.status === 'fulfilled' && Array.isArray(car.value.data)) {
      car.value.data.forEach((c: any) => list.push({
        key: `m${c.id}`, kind: 'mileage', date: c.date, title: c.destination || c.purpose || 'Mileage',
        distance: c.distance != null ? `${+Number(c.distance).toFixed(1)} ${c.unit || ''}`.trim() : undefined,
        amount: Number(c.cost) || 0, status: c.status,
      }));
    }
    list.sort((a, b) => String(b.date).localeCompare(String(a.date)));
    setRows(list);
  }, [canExp, canCar]);

  useEffect(() => { if (auth) load(); }, [auth, load]);

  // Fresh numbers when the user comes back to the tab (e.g. after adding a claim on Finance)
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === 'visible' && auth) load(); };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [auth, load]);

  // As many rows as fit the free height
  const listRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(4);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const rowH = (el.querySelector('.ex-row') as HTMLElement | null)?.offsetHeight || ROW_H;
      setFit(Math.max(1, Math.floor(el.clientHeight / rowH)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const money = (n: number) => {
    try {
      return currency ? new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(n) : n.toFixed(2);
    } catch { return n.toFixed(2); }
  };
  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  // Add opens the existing forms on the Finance page (?add=expense | mileage)
  const add = (kind: 'expense' | 'mileage') => { setMenuOpen(false); navigate(`/finance?add=${kind}`); };
  const onAddClick = () => {
    if (canExp && canCar) setMenuOpen(o => !o);
    else add(canExp ? 'expense' : 'mileage');
  };

  return (
    <div className="dashboard-card ex-card">
      <div className="ex-head">
        <div className="ex-ic"><ReceiptLongOutlinedIcon /></div>
        <span className="ex-title">Expenses</span>
        <div style={{ flex: 1 }} />
        <div className="ex-add-wrap" ref={menuRef}>
          <button type="button" className="ex-add" onClick={onAddClick}>
            <AddIcon /><span className="ex-add-t">Expense</span>
          </button>
          {menuOpen && (
            <div className="ex-menu">
              <button type="button" onClick={() => add('expense')}><ReceiptLongOutlinedIcon />Expense</button>
              <button type="button" onClick={() => add('mileage')}><DirectionsCarOutlinedIcon />Mileage</button>
            </div>
          )}
        </div>
      </div>

      <div className="ex-list" ref={listRef}>
        {rows.length === 0 && <div className="ex-empty">No expenses yet</div>}
        {rows.slice(0, fit).map(r => {
          const st = STATUS[r.status] ?? { label: r.status, cls: 'p' };
          return (
            <div key={r.key} className={`ex-row${r.status === 'rejected' ? ' rej' : ''}`}
              title={r.status === 'rejected' && r.reason ? `Rejected: ${r.reason}` : undefined}>
              <span className={`ex-ty ${r.kind === 'mileage' ? 'm' : 'e'}`}>
                {r.kind === 'mileage' ? <DirectionsCarOutlinedIcon /> : <ReceiptLongOutlinedIcon />}
              </span>
              <div className="ex-l2">
                <div className="ex-l2a">
                  <span className="ex-name">{r.title}{r.distance && <small> {r.distance}</small>}</span>
                  <span className="ex-amt">{money(r.amount)}</span>
                </div>
                <div className="ex-l2b">{fmtDate(r.date)} · <b className={`s-${st.cls}`}>{st.label}</b></div>
              </div>
            </div>
          );
        })}
      </div>

      <Link to="/finance" className="ex-link">View all <ArrowForwardIcon /></Link>
    </div>
  );
};

export default ExpensesCard;
